import { FormActions } from "../../components/common/FormActions";
import {
  SeverityTag,
  TransactionStatusTag,
} from "../../components/common/registerUtils";
import { createViewAction, formatDateTime } from "../../utils/helpers";
import { URL_FRAUD_CASES } from "../../utils/urls";
import { Tag } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import useGet from "../common/useGet";
import { API_FRAUD_CASES } from "../../utils/apis";
import useSet from "../common/useSet";
import { CFormTextarea, CButton, CSpinner } from "@coreui/react";
import { blueColor } from "../../components/styled/Theme";
import toast from "react-hot-toast";

const url = URL_FRAUD_CASES;

const FraudCaseTimeline = ({ caseId }: { caseId: string }) => {
  const { data: timelineData, loadData: loadTimeline } = useGet();

  useEffect(() => {
    if (caseId) {
      loadTimeline({ api: `${API_FRAUD_CASES}${caseId}/timeline/` });
    }
  }, [caseId]);

  // Define all stages in order
  const allStages = [
    {
      type: "event",
      label: "Event",
      defaultDescription: "Fraud Event Detected",
    },
    { type: "alert", label: "Alert", defaultDescription: "Alert Generated" },
    { type: "note", label: "Note", defaultDescription: "Investigation Notes" },
    { type: "outcome", label: "Outcome", defaultDescription: "Case Resolved" },
  ];

  // Get the timeline data and determine which stages are active
  const timeline = timelineData?.timeline || [];
  const activeStageTypes = new Set(timeline.map((item: any) => item.type));

  // Find the highest stage index that exists in the timeline
  const currentStageIndex =
    timeline.length > 0
      ? Math.max(
          ...allStages.map((stage, idx) =>
            activeStageTypes.has(stage.type) ? idx : -1,
          ),
        )
      : -1;

  const getTimelineConfig = (
    type: string,
    isCompleted: boolean,
    isCurrent: boolean,
  ) => {
    const opacity = isCompleted || isCurrent ? 1 : 0.3;
    const baseConfig = {
      event: {
        dot: <ClockCircleOutlined style={{ fontSize: "16px", opacity }} />,
        color: isCompleted || isCurrent ? "blue" : "gray",
      },
      alert: {
        dot: <WarningOutlined style={{ fontSize: "16px", opacity }} />,
        color: isCompleted || isCurrent ? "orange" : "gray",
      },
      note: {
        dot: <FileTextOutlined style={{ fontSize: "16px", opacity }} />,
        color: isCompleted || isCurrent ? "gray" : "lightgray",
      },
      outcome: {
        dot: <CheckCircleOutlined style={{ fontSize: "16px", opacity }} />,
        color: isCompleted || isCurrent ? "green" : "gray",
      },
    };
    return baseConfig[type as keyof typeof baseConfig] || baseConfig.event;
  };

  // Build custom timeline with alternating boxes
  const timelineContent = (
    <div
      style={{ position: "relative", padding: "80px 20px", minHeight: "250px" }}
    >
      {/* Horizontal line */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "20px",
          right: "20px",
          height: "2px",
          backgroundColor: "#d9d9d9",
          zIndex: 0,
        }}
      />

      {/* Timeline items */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "20px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {allStages.map((stage, index) => {
          // Check if this stage exists in the timeline
          const stageExists = activeStageTypes.has(stage.type);
          const isCompleted = stageExists && index < currentStageIndex;
          const isCurrent = stageExists && index === currentStageIndex;
          const isPending = !stageExists;
          const isAlternate = index % 2 === 1; // Alternate position

          // Find actual data for this stage
          const stageData = timeline.find(
            (item: any) => item.type === stage.type,
          );
          const config = getTimelineConfig(
            stage.type,
            isCompleted || isCurrent,
            isCurrent,
          );

          return (
            <div
              key={stage.type}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                minHeight: "140px",
              }}
            >
              {/* Content box - positioned above or below */}
              <div
                style={{
                  position: "absolute",
                  top: isAlternate ? "calc(50% + 20px)" : "auto",
                  bottom: isAlternate ? "auto" : "calc(50% + 20px)",
                  left: "0",
                  right: "0",
                  opacity: isPending ? 0.5 : 1,
                }}
              >
                {/* Notch arrow */}
                <div
                  style={{
                    position: "absolute",
                    [isAlternate ? "top" : "bottom"]: "-8px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "0",
                    height: "0",
                    borderLeft: "8px solid transparent",
                    borderRight: "8px solid transparent",
                    [isAlternate ? "borderBottom" : "borderTop"]: `8px solid ${
                      isPending ? "#d9d9d9" : "#91d5ff"
                    }`,
                  }}
                />

                {/* Content card */}
                <div
                  style={{
                    border: `1px solid ${isPending ? "#d9d9d9" : "#91d5ff"}`,
                    borderRadius: "8px",
                    padding: "10px",
                    backgroundColor: isPending ? "#f5f5f5" : "#e6f7ff",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    minHeight: "70px",
                    maxHeight: "150px",
                    overflowY: "auto",
                  }}
                >
                  {/* <div
                    style={{
                      fontSize: "10px",
                      color: "#999",
                      marginBottom: "4px",
                    }}
                  >
                    {stageData?.timestamp
                      ? formatDateTime(stageData.timestamp)
                      : stage.label}
                  </div> */}

                  {/* Handle outcome stage specially */}
                  {stage.type === "outcome" && stageData?.items?.outcome ? (
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "12px",
                          marginBottom: "4px",
                          color: "#333",
                        }}
                      >
                        Status: {stageData.items.outcome}
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#666",
                        }}
                      >
                        {formatDateTime(stageData.items.timestamp)}
                      </div>
                    </div>
                  ) : Array.isArray(stageData?.items) &&
                    stageData.items.length > 0 ? (
                    /* Display items array */
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {stageData.items.map((item: any, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            borderBottom:
                              idx < stageData.items.length - 1
                                ? "1px solid #d9d9d9"
                                : "none",
                            paddingBottom:
                              idx < stageData.items.length - 1 ? "8px" : "0",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "500",
                              fontSize: "11px",
                              marginBottom: "2px",
                              color: "#333",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.description || item.id || `Item ${idx + 1}`}
                          </div>
                          {item.author && (
                            <div style={{ fontSize: "9px", color: "#666" }}>
                              By: {item.author}
                            </div>
                          )}
                          {item.timestamp && (
                            <div style={{ fontSize: "9px", color: "#999" }}>
                              {formatDateTime(item.timestamp)}
                            </div>
                          )}
                          {(item.priority ||
                            item.fraud_score !== undefined ||
                            item.status) && (
                            <div
                              style={{
                                marginTop: "2px",
                                display: "flex",
                                gap: "4px",
                                flexWrap: "wrap",
                              }}
                            >
                              {item.priority && (
                                <Tag
                                  color={
                                    item.priority === "HIGH" ||
                                    item.priority === "CRITICAL"
                                      ? "red"
                                      : item.priority === "MEDIUM"
                                        ? "orange"
                                        : "default"
                                  }
                                  style={{ fontSize: "9px", margin: 0 }}
                                >
                                  Priority: {item.priority}
                                </Tag>
                              )}
                              {item.fraud_score !== undefined && (
                                <Tag
                                  color={
                                    item.fraud_score > 50 ? "red" : "green"
                                  }
                                  style={{ fontSize: "9px", margin: 0 }}
                                >
                                  Score: {item.fraud_score}
                                </Tag>
                              )}
                              {item.status && (
                                <Tag
                                  color={
                                    item.status === "OPEN" ? "blue" : "default"
                                  }
                                  style={{ fontSize: "9px", margin: 0 }}
                                >
                                  Status: {item.status}
                                </Tag>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Default display */
                    <div
                      style={{
                        fontWeight: isCurrent ? "600" : "500",
                        fontSize: "12px",
                        marginBottom: "4px",
                        color: "#333",
                        wordBreak: "break-word",
                      }}
                    >
                      {stageData?.description || stage.defaultDescription}
                    </div>
                  )}
                </div>
              </div>

              {/* Dot on the line */}
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  backgroundColor:
                    isCompleted || isCurrent ? config.color : "#d9d9d9",
                  border: "3px solid white",
                  boxShadow: "0 0 0 2px #d9d9d9",
                  zIndex: 2,
                }}
              />

              {/* Stage label - opposite side of content */}
              <div
                style={{
                  position: "absolute",
                  top: isAlternate ? "auto" : "calc(50% + 25px)",
                  bottom: isAlternate ? "calc(50% + 25px)" : "auto",
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: isCompleted || isCurrent ? "#333" : "#999",
                  opacity: isPending ? 0.5 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {stage.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (!timelineData) {
    return (
      <div
        className="whiteBg radius10"
        style={{ padding: "20px", textAlign: "center", color: "#999" }}
      >
        Loading timeline...
      </div>
    );
  }

  return (
    <div
      className="whiteBg radius10"
      style={{ padding: "15px 20px 20px 20px" }}
    >
      <div
        style={{
          padding: "0 0 15px 0",
          borderBottom: "1px solid #eee",
          marginBottom: "15px",
        }}
      >
        <div className="capitalize bold">Fraud Case Timeline</div>
      </div>
      {timelineContent}
    </div>
  );
};

export const useFraudCases = ({ id }: { id?: string } = {}) => {
  const tableColumns = () => [
    { id: "case_id", header: "Case ID", filter: { type: "text" } },
    { id: "title", header: "Title" },
    { id: "status", header: "Status" },
    { id: "outcome", header: "Outcome" },
    { id: "severity", header: "Severity" },
    { id: "estimated_loss", header: "Estimated loss" },
    { id: "recovered_amount", header: "Recovered amount" },
    { id: "assigned_to", header: "Assigned To" },
    { id: "team", header: "Team" },
    { id: "events_count", header: "Events" },
    { id: "alerts_count", header: "Alerts" },
    { id: "days_open", header: "Days Open" },
    { id: "created_at", header: "Created at" },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) => {
    return data?.map((item: any) => {
      return {
        ...item,
        severity: <SeverityTag severity={item.severity} />,
        outcome: <TransactionStatusTag status={item.outcome} />,
        created_at: formatDateTime(item.created_at),
        actions: <FormActions actions={[createViewAction(url, item?.id)]} />,
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    return {
      sections: [
        {
          component: id ? <FraudCaseTimeline caseId={id} /> : null,
        },
        {
          title: "Fraud Case Details",
          fields: {
            case_id: item.case_id,
            title: item.title,
            status: item.status,
            outcome: <TransactionStatusTag status={item.outcome} />,
            severity: <SeverityTag severity={item.severity} />,
            estimated_loss: item.estimated_loss,
            recovered_amount: item.recovered_amount,
            assigned_to: item.assigned_to,
            team: item.team,
            events_count: item.events_count,
            alerts_count: item.alerts_count,
            days_open: item.days_open,
            created_at: formatDateTime(item.created_at),
            updated_at: formatDateTime(item.updated_at),
            closed_at: formatDateTime(item.closed_at),
          },
        },
        {
          title: "Notes",
          fields: item?.notes?.length
            ? item.notes
            : { message: "No notes available" },
        },
        {
          title: "Add Notes",
          component: <AddNoteForm caseId={item?.id || ""} />,
        },
        {
          title: "Created By",
          fields: {
            phone_number: item?.created_by?.phone_number || "N/A",
            first_name: item?.created_by?.first_name || "N/A",
            last_name: item?.created_by?.last_name || "N/A",
            role: item?.created_by?.role || "N/A",
          },
        },
      ],
    };
  };

  return { tableColumns, tableData, viewData };
};

const AddNoteForm = ({ caseId }: { caseId: string }) => {
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState<string[]>([]);
  const {
    uploadData: submitFraudNotes,
    submitting: submittingNotes,
    error: submitError,
    success,
  } = useSet();

  const handleAddNote = () => {
    if (note.trim()) {
      setNotes([...notes, note.trim()]);
      setNote("");
    }
  };

  const handleRemoveNote = (index: number) => {
    setNotes(notes.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (notes.length === 0) return;

    submitFraudNotes({
      api: `${API_FRAUD_CASES}${caseId}/`,
      params: {
        notes: notes.map((n) => ({ note: n })),
      },
      method: "PATCH",
    });
  };

  useEffect(() => {
    if (success) {
      setNotes([]);
      setNote("");
      // Reload the page to show the new notes
      window.location.reload();
    }
  }, [success]);

  useEffect(() => {
    if (
      submitError &&
      Object.keys(submitError).length > 0 &&
      !submitError.notSubmit
    ) {
      const errorMessage =
        (typeof submitError?.message === "string"
          ? submitError.message
          : null) ||
        (typeof submitError?.detail === "string" ? submitError.detail : null) ||
        "Failed to add note";
      toast.error(errorMessage);
    }
  }, [submitError]);

  return (
    <div className="whiteBg radius10" style={{ padding: "20px" }}>
      <div
        style={{
          padding: "0 0 15px 0",
          borderBottom: "1px solid #eee",
          marginBottom: "15px",
        }}
      >
        <div className="capitalize bold">Add Notes</div>
      </div>
      <div style={{ display: "flex", gap: "10px" }}>
        <div style={{ marginBottom: "15px", flex: 1 }}>
          <CFormTextarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter your note here..."
            rows={4}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid #d9d9d9",
              fontSize: "14px",
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />
        </div>
        <div style={{ marginBottom: "15px" }}>
          <button
            onClick={handleAddNote}
            disabled={!note.trim()}
            style={{
              padding: "6px 10px",
              backgroundColor: note.trim() ? blueColor : "#d9d9d9",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: note.trim() ? "pointer" : "not-allowed",
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            + Add Note
          </button>
        </div>
      </div>

      {notes.length > 0 && (
        <div style={{ marginBottom: "15px" }}>
          <label
            className="capitalize font12 mb-1"
            style={{ display: "block", marginBottom: "8px" }}
          >
            Notes to Submit ({notes.length})
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {notes.map((n, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "#e6f7ff",
                  border: "1px solid #91d5ff",
                  borderRadius: "6px",
                  padding: "4px 8px",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  maxWidth: "100%",
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "300px",
                  }}
                >
                  {n}
                </span>
                <button
                  onClick={() => handleRemoveNote(index)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#ff4d4f",
                    cursor: "pointer",
                    fontSize: "12px",
                    padding: "0",
                    lineHeight: "1",
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <CButton
          onClick={handleSubmit}
          disabled={notes.length === 0 || submittingNotes}
          style={{
            padding: "8px 16px",
            backgroundColor:
              notes.length > 0 && !submittingNotes ? blueColor : "#d9d9d9",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor:
              notes.length > 0 && !submittingNotes ? "pointer" : "not-allowed",
            fontSize: "12px",
            fontWeight: "500",
          }}
        >
          {submittingNotes ? (
            <span>
              Submitting...
              <CSpinner size="sm" />
            </span>
          ) : (
            `Submit ${notes.length} Note(s)`
          )}
        </CButton>
      </div>
    </div>
  );
};
